import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  MessageSquare,
  CircleHelp,
  MessagesSquare,
  BellRing,
} from "lucide-react";
import { useTranslation } from "react-i18next";

function PanelShell({
  title,
  meta,
  actionLabel,
  onAction,
  className = "",
  delayClass = "",
  children,
}) {
  return (
    <section className={`lender-dashboard__panel ${className} ${delayClass}`}>
      <div className="lender-dashboard__panel-header">
        <div className="lender-dashboard__panel-header-copy">
          <h2 className="lender-dashboard__panel-title">{title}</h2>

          {meta ? (
            <span className="lender-dashboard__panel-meta">{meta}</span>
          ) : null}
        </div>

        {actionLabel ? (
          <button
            type="button"
            className="lender-dashboard__panel-action"
            onClick={onAction}
          >
            {actionLabel}
          </button>
        ) : null}
      </div>

      <div className="lender-dashboard__panel-body">{children}</div>
    </section>
  );
}

/* ==========================================================
   EMPTY INFO CARD
   ========================================================== */

function EmptyInfoCard({ icon, title, description, delayClass = "" }) {
  return (
    <div className={`lender-dashboard__info-card ${delayClass}`}>
      <div className="lender-dashboard__info-icon-wrap">{icon}</div>

      <div className="lender-dashboard__info-copy">
        <h4 className="lender-dashboard__info-title">{title}</h4>

        <p className="lender-dashboard__info-description">{description}</p>
      </div>
    </div>
  );
}

/* ==========================================================
   MESSAGE HELPERS
   ========================================================== */

function getSenderName(item, t) {
  return (
    item?.sender_name ||
    item?.sender_role ||
    t("messagesPanel.list.unknownSender")
  );
}

function getSenderBadge(item, t) {
  const senderRole = item?.sender_role?.toUpperCase?.();

  if (senderRole === "OSS") {
    return "OSS";
  }

  if (senderRole === "HOMEOWNER" || senderRole === "HOMEBUYER") {
    return "HB";
  }

  if (senderRole === "LENDER") {
    return "LN";
  }

  const name = getSenderName(item, t);

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

function getMessageContext(item) {
  return (
    item?.loan_case_reference || item?.project_code || item?.dossier_code || ""
  );
}

function formatCreatedAt(value, t) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const now = new Date();

  const diffMs = now.getTime() - date.getTime();

  const diffMinutes = Math.max(1, Math.floor(diffMs / 60000));

  const diffHours = Math.floor(diffMinutes / 60);

  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 60) {
    return t("messagesPanel.list.updatedMinutesAgo", {
      count: diffMinutes,
    });
  }

  if (diffHours < 24) {
    return t("messagesPanel.list.updatedHoursAgo", {
      count: diffHours,
    });
  }

  if (diffDays < 7) {
    return t("messagesPanel.list.updatedDaysAgo", {
      count: diffDays,
    });
  }

  return date.toLocaleDateString();
}

function truncateMessage(value, maxLength = 180) {
  if (!value) {
    return "";
  }

  const clean = String(value).replace(/\s+/g, " ").trim();

  if (clean.length <= maxLength) {
    return clean;
  }

  return `${clean.slice(0, maxLength).trim()}…`;
}

/* ==========================================================
   MESSAGE AVATAR
   ========================================================== */

function MessageAvatar({ item, t }) {
  const badge = getSenderBadge(item, t);

  const imageUrl = item?.sender_avatar_url || "";

  const altText = getSenderName(item, t);

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={altText}
        className="
          lender-dashboard__message-avatar
          lender-dashboard__message-avatar--image
        "
      />
    );
  }

  return <div className="lender-dashboard__message-avatar">{badge}</div>;
}

/* ==========================================================
   MESSAGE ROW
   ========================================================== */

function MessageRow({ item, t, onOpen }) {
  const createdLabel = formatCreatedAt(item?.created_at, t);

  const senderName = getSenderName(item, t);

  const context = getMessageContext(item);

  const isUnread = item?.is_read === false;

  const metaParts = [context, senderName, createdLabel].filter(Boolean);

  const title = item?.thread_title || t("messagesPanel.list.defaultTitle");

  const preview =
    truncateMessage(item?.body) || t("messagesPanel.list.noPreview");

  return (
    <div
      className={[
        "lender-dashboard__data-row",
        "lender-dashboard__data-row--message",
        isUnread ? "lender-dashboard__data-row--unread" : "",
      ].join(" ")}
    >
      <div className="lender-dashboard__data-row-main">
        <MessageAvatar item={item} t={t} />

        <div className="lender-dashboard__data-row-copy">
          <div className="lender-dashboard__message-title-row">
            <h4 className="lender-dashboard__data-row-title">{title}</h4>

            {isUnread ? (
              <span
                className="lender-dashboard__message-unread-dot"
                aria-label={t("messagesPanel.list.unread")}
                title={t("messagesPanel.list.unread")}
              />
            ) : null}
          </div>

          <p className="lender-dashboard__data-row-description">{preview}</p>

          {metaParts.length > 0 ? (
            <p className="lender-dashboard__data-row-submeta">
              {metaParts.join(" • ")}
            </p>
          ) : null}
        </div>
      </div>

      <div className="lender-dashboard__data-row-actions">
        <button
          type="button"
          className="lender-dashboard__icon-button"
          aria-label={t("common.actions.reply")}
          onClick={() => onOpen(item)}
        >
          <MessageSquare size={16} />
        </button>

        <button
          type="button"
          className="lender-dashboard__icon-button"
          aria-label={t("common.actions.open")}
          onClick={() => onOpen(item)}
        >
          <ArrowUpRight size={16} />
        </button>
      </div>
    </div>
  );
}

/* ==========================================================
   LOADING
   ========================================================== */

function LoadingState() {
  return (
    <div className="lender-dashboard__shell-stack">
      <div className="lender-dashboard__placeholder-card">
        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--title" />

        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text" />

        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text-short" />
      </div>

      <div className="lender-dashboard__placeholder-card">
        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--title" />

        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text" />

        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text-short" />
      </div>

      <div className="lender-dashboard__placeholder-note" />
    </div>
  );
}

/* ==========================================================
   EMPTY STATE
   ========================================================== */

function EmptyState({ t }) {
  return (
    <>
      <div className="lender-dashboard__info-list">
        <EmptyInfoCard
          icon={<MessagesSquare size={18} strokeWidth={2} />}
          title={t("messagesPanel.empty.title1")}
          description={t("messagesPanel.empty.description1")}
          delayClass="lender-dashboard__fade-3"
        />

        <EmptyInfoCard
          icon={<BellRing size={18} strokeWidth={2} />}
          title={t("messagesPanel.empty.title2")}
          description={t("messagesPanel.empty.description2")}
          delayClass="lender-dashboard__fade-4"
        />
      </div>

      <div className="lender-dashboard__panel-note lender-dashboard__fade-4">
        <CircleHelp size={14} />

        <span>{t("messagesPanel.empty.footer")}</span>
      </div>
    </>
  );
}

/* ==========================================================
   LIST
   ========================================================== */

function ListState({ items, t, onOpen }) {
  return (
    <div className="lender-dashboard__data-list">
      {items.map((item, index) => (
        <MessageRow
          key={
            item?.notification_id ||
            item?.message_id ||
            item?.thread_id ||
            `message-${index}`
          }
          item={item}
          t={t}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}

/* ==========================================================
   MAIN PANEL
   ========================================================== */

export default function MessagesPanel({
  items = [],
  totalCount = 0,
  hasMore = false,
  isLoading = false,
}) {
  const { t } = useTranslation("lender-dashboard");

  const navigate = useNavigate();

  const hasItems = items.length > 0;

  const meta = hasItems
    ? t("messagesPanel.metaPreview", {
        shown: items.length,

        total: totalCount || items.length,
      })
    : t("messagesPanel.meta");

  function openCommunication(item) {
    const threadId = Number(item?.thread_id);

    if (!Number.isInteger(threadId) || threadId <= 0) {
      navigate("/lender/communication");

      return;
    }

    navigate(`/lender/communication?thread=${threadId}`);
  }

  return (
    <PanelShell
      title={t("messagesPanel.title")}
      meta={meta}
      actionLabel={hasItems || hasMore ? t("common.actions.viewAll") : ""}
      onAction={() => navigate("/lender/communication")}
      className="lender-dashboard__panel--messages"
      delayClass="lender-dashboard__fade-3"
    >
      {isLoading ? (
        <LoadingState />
      ) : hasItems ? (
        <ListState items={items} t={t} onOpen={openCommunication} />
      ) : (
        <EmptyState t={t} />
      )}
    </PanelShell>
  );
}
